import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import FormFieldWithLabel from '../../../common/FormFieldWithLabel';
import CustomSelect from '../../../../common/formElements/CustomSelect';
import CustomLoader from '../../../common/CustomLoader';
import { getPaperFormat } from '../../../../common/actions/pdf/pdfActions';
import {
  SEND_ITEM_ID_FROM_OPTIONS_DESC,
  sendItemIdFromOptions,
} from '../../../../common/utils/event_constants';
import BrowserStorageLocationField from '../BrowserStorageLocationField';
import { createActionSettingsSchema, validateItemIdBSLKey } from '../collection/Util';

export const DownloadPDFTemplate = ({
  hideModal,
  actions,
  eventAction,
  actionData,
  selectedEvent,
  showLoaderForAction,
  projectId,
  events,
  version,
}) => {
  const schema = createActionSettingsSchema(actionData, eventAction);
  // State
  const [advancedOptions, showAdvancedOptions] = useState(false);
  const [pdfTempCollection, setPdfTempCollection] = useState('');
  const [pdfHeaderCollection, setPdfHeaderCollection] = useState('');
  const [pdfFooterCollection, setPdfFooterCollection] = useState('');

  useEffect(() => {
    const { pdfTemplate, headerTemplate, footerTemplate } = schema;
    let templateCollection = getPDFbindCollectionName(pdfTemplate);
    setPdfTempCollection(templateCollection);
    let headerCollection = getPDFbindCollectionName(headerTemplate);
    setPdfHeaderCollection(headerCollection);
    let footerCollection = getPDFbindCollectionName(footerTemplate);
    setPdfFooterCollection(footerCollection);
  }, []);

  // Select Options
  const getPdfTemplateListFromSnippets = () => {
    let options = [];
    const { snippets } = events;
    const pdfSnippets = snippets.filter((snippet) => snippet.snippetType === 'PDF_TEMPLATE');
    pdfSnippets.forEach((snippet) => {
      options.push({
        value: snippet.uuid,
        label: snippet.name,
      });
    });

    return options;
  };

  const fetchCollectionList = () => {
    let options = [];
    const { collections, inMemoryCollections } = events;
    options = [...collections, ...inMemoryCollections].map((collection) => {
      return { value: collection.collectionName, label: collection.collectionTitle };
    });
    return options;
  };

  const fetchCollectionFieldList = (collectionValue, getDF = false) => {
    const { collections } = events;
    let options = [];
    if (collectionValue) {
      const selectedCollection = collections.find(
        (coll) => coll['collectionName'] === collectionValue,
      );
      const selectedCollectionFields = selectedCollection ? selectedCollection.fields : [];
      const fieldOptions = selectedCollectionFields.map((e) => ({
        value: e.fieldName,
        label: e.fieldTitle.en,
      }));
      options.push({ label: 'Collection Field', options: [...fieldOptions] });
      if (getDF) {
        const selectedCollectionDerivedFields = selectedCollection
          ? selectedCollection.utilities
          : [];
        const derivedFieldOptions = selectedCollectionDerivedFields.map((df) => ({
          value: `DF::${df.name}`,
          label: df.name,
        }));
        options.push({ label: 'Derived Field', options: [...derivedFieldOptions] });
      }
    }
    return options;
  };

  // Options
  const pdfSnippetOptions = getPdfTemplateListFromSnippets();
  const paperFormats = getPaperFormat();
  const collectionOptions = fetchCollectionList();

  const showAlertMessage = (message) => {
    return (
      <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12">
        <div className="alert alertWarning">
          <div className="alert-icon">
            <i
              className="wb webconnect-exclamation-circle-fill"
              style={{ verticalAlign: '1px' }}
            ></i>
          </div>
          {message}
        </div>
      </div>
    );
  };

  // Parameters Fields
  const renderSelectField = (param, values, setFieldValue, setFieldTouched) => {
    const { fieldName, label, options, info, defaultValue = '' } = param;
    let { handleChange = '' } = param;

    return (
      <div className="col-12">
        <div className="form-group">
          <FormFieldWithLabel
            cta={false}
            label={label}
            icon="select"
            infoIcon={true}
            infoDescription={info}
          >
            <CustomSelect
              name={fieldName}
              isMulti={false}
              onChange={setFieldValue}
              onBlur={setFieldTouched}
              options={options}
              value={values[fieldName] || defaultValue}
              onChangeValue={handleChange || ''}
            />
            <ErrorMessage name={fieldName}>
              {(msg) => <p className="text-error">{msg}</p>}
            </ErrorMessage>
          </FormFieldWithLabel>
        </div>
      </div>
    );
  };

  const renderBooleanField = (param, values) => {
    const { fieldName, label, info } = param;
    return (
      <div className="col-12">
        <div className="form-group">
          <FormFieldWithLabel
            cta={false}
            label=""
            icon="boolean-toggle"
            infoIcon={true}
            infoDescription={info}
          >
            <div className="form-check">
              <label className="form-check-label">
                <Field
                  type="checkbox"
                  name={fieldName}
                  className="form-check-input"
                  title={label}
                />
                {label}
                <span className="form-check-sign">
                  <span className="check"></span>
                </span>
              </label>
              <ErrorMessage name={fieldName}>
                {(msg) => <p className="text-error">{msg}</p>}
              </ErrorMessage>
            </div>
          </FormFieldWithLabel>
        </div>
      </div>
    );
  };

  const renderNumberField = (param, values) => {
    const { fieldName, label, info } = param;
    return (
      <div className="col-12">
        <div className="form-group">
          <FormFieldWithLabel
            cta={false}
            label={label}
            icon="number"
            infoIcon={true}
            infoDescription={info}
          >
            <Field
              type={'number'}
              name={fieldName}
              className="form-control form-control-sm"
              autoComplete="off"
              placeholder={label}
              value={values[fieldName]}
            />
            <ErrorMessage name={fieldName}>
              {(msg) => <p className="text-error">{msg}</p>}
            </ErrorMessage>
          </FormFieldWithLabel>
        </div>
      </div>
    );
  };

  const handlePdfCollectionChange = (name, value) => {
    let paramName = name[0];
    let collection = getPDFbindCollectionName(value);
    switch (paramName) {
      case 'pdfTemplate':
        setPdfTempCollection(collection);
        break;
      case 'headerTemplate':
        setPdfHeaderCollection(collection);
        break;
      case 'footerTemplate':
        setPdfFooterCollection(collection);
        break;
      default:
        break;
    }
  };

  const getPDFbindCollectionName = (templateId) => {
    if (!templateId) return '';
    const { snippets } = events;
    const pdfSnippet = snippets.find((snippet) => snippet.uuid === templateId);
    return pdfSnippet ? pdfSnippet.collectionId : '';
  };

  const renderParametersField = (values, setFieldValue, setFieldTouched) => {
    const collectionFieldOptions = fetchCollectionFieldList(values['collection']);
    const pdfCollectionFieldOptions = fetchCollectionFieldList(pdfTempCollection, true);

    const pdfTemplateParam = {
      fieldName: 'pdfTemplate',
      label: 'PDF Template',
      options: pdfSnippetOptions,
      info: '',
      handleChange: handlePdfCollectionChange,
    };
    const formatParam = {
      fieldName: 'format',
      label: 'Format',
      options: paperFormats,
      info: '',
      defaultValue: 'A4',
    };
    const printBackgroundParam = {
      fieldName: 'printBackground',
      label: 'Print Background',
      info: '',
    };

    const landscapeParam = { fieldName: 'landscape', label: 'Landscape', info: '' };
    const displayHeaderParam = { fieldName: 'displayHeader', label: 'Display Header', info: '' };
    const displayFooterParam = { fieldName: 'displayFooter', label: 'Display Footer', info: '' };
    const headerTemplateParam = {
      fieldName: 'headerTemplate',
      label: 'Header Template',
      options: pdfSnippetOptions,
      info: '',
      handleChange: handlePdfCollectionChange,
    };
    const footerTemplateParam = {
      fieldName: 'footerTemplate',
      label: 'Footer Template',
      options: pdfSnippetOptions,
      info: '',
      handleChange: handlePdfCollectionChange,
    };
    const marginTopParam = {
      fieldName: 'marginTop',
      label: 'Margin Top (in cm)',
      info: '',
    };
    const marginBottomParam = {
      fieldName: 'marginBottom',
      label: 'Margin Bottom (in cm)',
      info: '',
    };
    const marginLeftParam = {
      fieldName: 'marginLeft',
      label: 'Margin Left (in cm)',
      info: '',
    };
    const marginRightParam = {
      fieldName: 'marginRight',
      label: 'Margin Right (in cm)',
      info: '',
    };
    const saveToCollectionParam = {
      fieldName: 'saveToCollection',
      label: 'Add To Collection',
      info: '',
    };
    const downloadSavedPDFParam = {
      fieldName: 'downloadSavedPDF',
      label: 'Download Saved PDF',
      info: '',
    };

    const collectionParam = {
      fieldName: 'collection',
      label: 'Select Collection',
      options: collectionOptions,
      info: '',
    };
    const collectionFieldParam = {
      fieldName: 'collectionField',
      label: 'Select Field',
      options: collectionFieldOptions,
      info: '',
    };
    const generatePDFNameParam = {
      fieldName: 'generatePDFName',
      label: 'Generate PDF name using a collection field.',
    };
    const pdfNameFieldParam = {
      fieldName: 'pdfNameField',
      label: 'PDF Name Field',
      options: pdfCollectionFieldOptions,
      info: '',
    };
    let isHeaderMatch,
      isFooterMatch = '';
    if (pdfTempCollection !== null) {
      isHeaderMatch = pdfHeaderCollection !== null && pdfHeaderCollection === pdfTempCollection;
      isFooterMatch = pdfFooterCollection !== null && pdfFooterCollection === pdfTempCollection;
    }

    const renderSendItemIdFrom = (action, setFieldValue, setFieldTouched, values) => {
      // Fallback handling of Checkbox field values in the Dropdown
      if (!values['sendItemIdFrom']) {
        if (values['sendCurrentObjectID']) {
          values['sendItemIdFrom'] = 'sendCurrentObjectID';
          values['sendCurrentObjectID'] = '';
        } else if (values['sendCurrentPageItemId']) {
          values['sendItemIdFrom'] = 'sendCurrentPageItemId';
          values['sendCurrentPageItemId'] = '';
          // } else if (values['sendCurrentPageReferenceItemId']) {
          //   values['sendItemIdFrom'] = 'sendCurrentPageReferenceItemId';
          //   values['sendCurrentPageReferenceItemId'] = '';
        } else if (values['sendCurrentUserId']) {
          values['sendItemIdFrom'] = 'sendCurrentUserId';
          values['sendCurrentUserId'] = '';
        } else if (values['sendPreviousStepParentCollectionId']) {
          values['sendItemIdFrom'] = 'sendPreviousStepParentCollectionId';
          values['sendPreviousStepParentCollectionId'] = '';
        } else if (values['sendTargetItemID']) {
          values['sendItemIdFrom'] = 'sendTargetItemID';
          values['sendTargetItemID'] = '';
        }
      }

      return (
        <div className="form-group">
          <FormFieldWithLabel
            cta={false}
            label="Get Item ID From"
            icon="select"
            infoIcon={true}
            infoDescription={SEND_ITEM_ID_FROM_OPTIONS_DESC}
          >
            <CustomSelect
              name={'sendItemIdFrom'}
              isMulti={false}
              onChange={setFieldValue}
              onBlur={setFieldTouched}
              options={sendItemIdFromOptions()}
              value={values['sendItemIdFrom']}
            />
            <ErrorMessage name={'sendItemIdFrom'}>
              {(msg) => <p className="text-error">{msg}</p>}
            </ErrorMessage>
          </FormFieldWithLabel>
        </div>
      );
    };

    const isSendBSLItemIdEnabled = (values) => {
      return values['sendItemIdFrom'] === 'sendBSLItemId';
    };

    return (
      <>
        {renderSelectField(pdfTemplateParam, values, setFieldValue, setFieldTouched)}
        {renderSelectField(formatParam, values, setFieldValue, setFieldTouched)}
        <div className="col-12">
          {renderSendItemIdFrom(actionData, setFieldValue, setFieldTouched, values)}
          {isSendBSLItemIdEnabled(values) && (
            <>
              <div className="form-group">
                <FormFieldWithLabel
                  cta={false}
                  label="Item Collection"
                  icon="select"
                  infoIcon={true}
                  infoDescription={'Item Collection'}
                >
                  <CustomSelect
                    name={'bslCollection'}
                    isMulti={false}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    options={collectionOptions}
                    value={values['bslCollection']}
                  />
                  <ErrorMessage name={'bslCollection'}>
                    {(msg) => <p className="text-error">{msg}</p>}
                  </ErrorMessage>
                </FormFieldWithLabel>
              </div>
              {values['bslCollection'] && (
                <>
                  <BrowserStorageLocationField
                    fieldName={'browserStorageLocation'}
                    values={values}
                    description={
                      'Select a browser storage location to get an Item ID key (Default: Session Storage).'
                    }
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                  />
                  <div className="form-group">
                    <FormFieldWithLabel
                      cta={false}
                      label="Item ID JSON Key"
                      icon="text"
                      infoIcon={true}
                      infoDescription={`Enter the key which contains Item ID value in your web browser storage.`}
                    >
                      <Field
                        type="text"
                        name="itemIdBSLKey"
                        className="form-control form-control-sm"
                        autoComplete="off"
                        placeholder="Item ID JSON Key Eg. Key1 or, Parent_Key1.Child_Key[0]"
                        validate={validateItemIdBSLKey}
                      />
                    </FormFieldWithLabel>
                    <ErrorMessage name={'itemIdBSLKey'}>
                      {(msg) => <p className="text-error ml-5">{msg}</p>}
                    </ErrorMessage>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div className="form-group">
          <button
            onClick={() => showAdvancedOptions(!advancedOptions)}
            className="btn btn-icon-only btn-outline-info ml-5 p-1"
            title="Show Advanced Options"
            type="button"
          >
            <i className="wb webconnect-cog"></i>&nbsp;Advanced Options
          </button>
        </div>
        <>
          {advancedOptions && (
            <>
              {renderBooleanField(printBackgroundParam, values)}
              {renderBooleanField(landscapeParam, values)}
              {renderBooleanField(generatePDFNameParam, values)}
              {values['generatePDFName'] &&
                renderSelectField(pdfNameFieldParam, values, setFieldValue, setFieldTouched)}
              {renderBooleanField(displayHeaderParam, values)}

              {values['displayHeader'] && (
                <>
                  {renderSelectField(headerTemplateParam, values, setFieldValue, setFieldTouched)}
                  {!isHeaderMatch &&
                    showAlertMessage('Header collection must match the PDF template collection.')}
                </>
              )}
              {renderBooleanField(displayFooterParam, values)}
              {values['displayFooter'] && (
                <>
                  {renderSelectField(footerTemplateParam, values, setFieldValue, setFieldTouched)}
                  {!isFooterMatch &&
                    showAlertMessage('Footer collection must match the PDF template collection.')}
                </>
              )}
              {renderNumberField(marginTopParam, values)}
              {renderNumberField(marginBottomParam, values)}
              {renderNumberField(marginLeftParam, values)}
              {renderNumberField(marginRightParam, values)}
              {renderBooleanField(saveToCollectionParam, values)}
              {values['saveToCollection'] && (
                <>
                  {renderSelectField(collectionParam, values, setFieldValue, setFieldTouched)}
                  {values['collection'] && (
                    <>
                      {renderSelectField(
                        collectionFieldParam,
                        values,
                        setFieldValue,
                        setFieldTouched,
                      )}
                    </>
                  )}
                  {renderBooleanField(downloadSavedPDFParam, values)}
                </>
              )}
            </>
          )}
        </>
      </>
    );
  };

  const handleSubmit = (values) => {
    let parameters = [];
    Object.keys(values).map((value) => {
      const action = actionData.args.find((param) => {
        return param.name === value;
      });
      parameters.push({ name: value, value: values[value], type: action.type });
    });

    let actionFormData = eventAction;
    actionFormData.parameters = parameters;
    actions.updateAction({
      projectId,
      version,
      eventId: selectedEvent.uuid,
      data: actionFormData,
    });
    hideModal();
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={schema}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        handleSubmit(values);
      }}
    >
      {({ isSubmitting, values, setFieldValue, setFieldTouched, errors }) => (
        <Form>
          <div className="row">{renderParametersField(values, setFieldValue, setFieldTouched)}</div>
          <div className="row">
            <div className="col-md-6 text-left">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={hideModal}
              >
                <span>
                  <i className="wb webconnect-times"></i>&nbsp;Cancel
                </span>
              </button>
            </div>
            <div className="col-md-6 text-right">
              <button type="submit" className="btn btn-theme-submit btn-sm">
                <i className="wb webconnect-save-regular"></i>&nbsp;Save&nbsp;
                <CustomLoader loading={showLoaderForAction} />
              </button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};
